import { FC, useEffect, useRef, useState } from 'react';
import {
  Cropper,
  CropperPreview,
  CropperPreviewRef,
  CropperRef,
  CropperState,
} from 'react-advanced-cropper';
import { useTranslation } from 'react-i18next';
import {
  Box,
  BoxProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
  Typography,
} from '@mui/material';

import { Button, Icon, IconOption } from '@components/base';
import { ImageCropAspectRatio } from '@enums/ImageCropAspectRatio';
import useImageGroupData from '@hooks/imageHooks/useImageGroupData';
import { getAspectRatio, getAspectRatioStr } from '@models/Image';

import 'react-advanced-cropper/dist/style.css';
import 'react-advanced-cropper/dist/themes/corners.css';

type ImageCropperProps = {
  initCrop?: ImageCropAspectRatio;
  onCancel: VoidFunction;
  onCrop: (canvas?: HTMLCanvasElement | null) => void;
  src: string;
};

type AspectRatioProps = BoxProps & {
  ratio: { height: number; width: number };
};

const AspectRatioIcon = styled(({ ratio, ...props }: AspectRatioProps) => <Box {...props} />)`
  aspect-ratio: ${({ ratio: { height, width } }) => `${width} / ${height}`};
  border: 2px solid currentColor;
  border-radius: 2px;
  bottom: 0;
  left: 0;
  margin: auto;
  max-height: 100%;
  max-width: 100%;
  position: absolute;
  right: 0;
  top: 0;
`;

export const ImageCropper: FC<ImageCropperProps> = ({ initCrop, onCrop, onCancel, src }) => {
  const { t } = useTranslation();

  const { defaultImageAspectRatio } = useImageGroupData();

  const [aspectRatio, setAspectRatio] = useState(
    initCrop ?? defaultImageAspectRatio ?? ImageCropAspectRatio.FREE
  );

  const previewRef = useRef<CropperPreviewRef>(null);
  const cropperRef = useRef<CropperRef>(null);

  const aspectRatioDefs: Record<
    ImageCropAspectRatio,
    { icon?: IconOption; label: string; ratio: { height: number; width: number } | false }
  > = {
    [ImageCropAspectRatio.FREE]: { label: t('free'), ratio: false, icon: 'cropFree' },
    [ImageCropAspectRatio.CROP_1_1]: {
      label: getAspectRatioStr(ImageCropAspectRatio.CROP_1_1, ' : '),
      ratio: getAspectRatio(ImageCropAspectRatio.CROP_1_1),
    },
    [ImageCropAspectRatio.CROP_16_9]: {
      label: getAspectRatioStr(ImageCropAspectRatio.CROP_16_9, ' : '),
      ratio: getAspectRatio(ImageCropAspectRatio.CROP_16_9),
    },
    [ImageCropAspectRatio.CROP_21_9]: {
      label: getAspectRatioStr(ImageCropAspectRatio.CROP_21_9, ' : '),
      ratio: getAspectRatio(ImageCropAspectRatio.CROP_21_9),
    },
    [ImageCropAspectRatio.CROP_30_9]: {
      label: getAspectRatioStr(ImageCropAspectRatio.CROP_30_9, ' : '),
      ratio: getAspectRatio(ImageCropAspectRatio.CROP_30_9),
    },
    [ImageCropAspectRatio.CROP_2_3]: {
      label: getAspectRatioStr(ImageCropAspectRatio.CROP_2_3, ' : '),
      ratio: getAspectRatio(ImageCropAspectRatio.CROP_2_3),
    },
  };

  const onUpdate = () => previewRef.current?.refresh();

  const getAspectRatioValue = (ratio: { height: number; width: number } | false) =>
    ratio ? ratio.width / ratio.height : undefined;

  const aspectRatioValue = getAspectRatioValue(aspectRatioDefs[aspectRatio].ratio);

  useEffect(() => {
    cropperRef.current?.refresh();
    previewRef.current?.refresh();

    if (aspectRatio !== ImageCropAspectRatio.FREE) {
      cropperRef.current?.setCoordinates((state: CropperState) => ({
        width: state.imageSize.width,
        height: state.imageSize.height,
        left: (state.imageSize.width - (state.coordinates?.width || 0)) / 2,
        top: (state.imageSize.height - (state.coordinates?.height || 0)) / 2,
      }));
    }
  }, [aspectRatio]);

  return (
    <Dialog fullWidth maxWidth='lg' onClose={onCancel} open transitionDuration={0}>
      <DialogTitle>{t('cropImage')}</DialogTitle>
      <DialogContent>
        <Stack maxWidth={1200} pb={2} spacing={1}>
          <Stack direction='row' spacing={1}>
            {Object.entries(aspectRatioDefs).map(([ar, { label, ratio, icon }]) => {
              const value = getAspectRatioValue(ratio);

              return (
                <Button
                  color='inherit'
                  key={ar}
                  onClick={() => setAspectRatio(ar as unknown as ImageCropAspectRatio)}
                  sx={
                    aspectRatio === (ar as ImageCropAspectRatio)
                      ? { background: theme => theme.palette.action.selected }
                      : {}
                  }
                >
                  <Stack alignItems='center' spacing={1}>
                    {icon ? (
                      <Icon
                        name={icon}
                        sx={value && Number(value) < 1 ? { rotate: '90deg' } : undefined}
                      />
                    ) : ratio ? (
                      <Box height={20} position='relative' width={20}>
                        <AspectRatioIcon ratio={ratio} />
                      </Box>
                    ) : null}
                    {label}
                  </Stack>
                </Button>
              );
            })}
          </Stack>

          <Stack direction='row' mb={2} spacing={2}>
            <Box
              maxWidth={theme => `calc(60% - ${theme.spacing()})`}
              p={2}
              sx={{
                overflow: 'hidden',
                '.advanced-cropper': {
                  overflow: 'visible !important',
                },
              }}
            >
              <Box maxWidth={1} width={1}>
                <Cropper
                  className='cropper'
                  defaultCoordinates={(state: CropperState) => ({
                    height: state.imageSize.height,
                    width: state.imageSize.width,
                  })}
                  onUpdate={onUpdate}
                  ref={cropperRef}
                  src={src}
                  stencilProps={{ aspectRatio: aspectRatioValue }}
                  transitions={false}
                />
              </Box>
            </Box>

            <Box
              maxHeight='90%'
              maxWidth={theme => `calc(40% - ${theme.spacing()})`}
              pb={2}
              sx={{ '*': { maxHeight: 'auto !important' } }}
            >
              <Typography gutterBottom variant='h5'>
                {t('preview')}
              </Typography>

              <CropperPreview className='preview' cropper={cropperRef} ref={previewRef} />
            </Box>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button onClick={() => onCrop(cropperRef.current?.getCanvas())} variant='contained'>
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropper;
